import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import baseService from '../../services/base';
import { fetchData } from '../../services/fetch';
import Loader from '../../components/Feedback/Loader';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import { AttentionModal } from '../../components/Modals';
import ComparisonGroupModal, { MINIMO_DE_EMPRESAS } from './ComparisonGroupModal';

/**
 * GRI-06 — CA01: os grupos que definem o "mercado" de cada empresa.
 *
 * É o que o benchmarking do portal (EG-50) lê para saber contra quem comparar.
 * Grupo abaixo do mínimo aparece sinalizado: ele existe, mas não anonimiza, e
 * por isso fica sem comparação até ser completado.
 */
export default function ComparisonGroups() {
  const toast = useToast();

  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState(null);
  const [formAberto, setFormAberto] = useState(false);
  const [emExclusao, setEmExclusao] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  const {
    data: grupos,
    isLoading,
    error,
    mutate,
  } = useSWR('/comparison-group', fetchData);

  // São poucos grupos: filtrar em memória evita uma ida ao servidor a cada
  // tecla.
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return grupos || [];

    return (grupos || []).filter((grupo) =>
      grupo.name?.toLowerCase().includes(termo)
    );
  }, [grupos, busca]);

  function abrirCriacao() {
    setEditando(null);
    setFormAberto(true);
  }

  function abrirEdicao(grupo) {
    setEditando(grupo);
    setFormAberto(true);
  }

  async function confirmarExclusao() {
    setExcluindo(true);
    try {
      await baseService.delete(
        `/comparison-group/${emExclusao.id_comparison_group}`
      );
      toast({ title: 'Grupo excluído', status: 'success' });
      await mutate();
    } catch {
      toast({ title: 'Não foi possível excluir o grupo', status: 'error' });
    } finally {
      setExcluindo(false);
      setEmExclusao(null);
    }
  }

  return (
    <MainContent>
      <PageHeaderStructure
        title='Grupos de Comparação'
        subtitle='Define quais empresas são comparadas entre si nos dashboards de benchmarking.'
      >
        <Button
          variant='primary'
          width='230px'
          height='59px'
          onClick={abrirCriacao}
        >
          Novo Grupo
        </Button>
      </PageHeaderStructure>

      <Flex gap='12px' flexWrap='wrap' align='flex-end' mt='24px' mb='16px'>
        <Box w='320px'>
          <Text fontSize='xs' color='#818A8F' mb='8px'>
            Buscar
          </Text>
          <Input
            placeholder='Nome do grupo'
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            bg='white'
          />
        </Box>

        <Text fontSize='sm' color='#818A8F' ml='auto'>
          {filtrados.length} de {grupos?.length || 0} grupos
        </Text>
      </Flex>

      {error && <Text color='red.600'>Erro ao carregar os grupos.</Text>}

      {isLoading ? (
        <Loader />
      ) : (
        <TableContainer bg='white' borderRadius='12px' p='8px'>
          <Table variant='simple'>
            <Thead>
              <Tr>
                <Th>Nome do Grupo</Th>
                <Th>Empresas</Th>
                <Th isNumeric>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtrados.map((grupo) => (
                <Tr key={grupo.id_comparison_group}>
                  <Td whiteSpace='normal'>
                    <Text fontWeight={600}>{grupo.name}</Text>
                    {!!grupo.description && (
                      <Text fontSize='xs' color='#818A8F'>
                        {grupo.description}
                      </Text>
                    )}
                  </Td>
                  <Td whiteSpace='nowrap'>
                    <Flex align='center' gap='8px'>
                      <Text>{grupo.total_empresas}</Text>
                      {/* O grupo degradado não some nem trava o admin — mas
                          não pode passar despercebido: sem 3 empresas ele
                          fica sem benchmarking. */}
                      {grupo.abaixo_do_minimo && (
                        <Tooltip
                          hasArrow
                          label={`Sem comparação de mercado: são necessárias ao menos ${MINIMO_DE_EMPRESAS} empresas para preservar o anonimato.`}
                        >
                          <Badge colorScheme='orange' textTransform='none'>
                            abaixo do mínimo
                          </Badge>
                        </Tooltip>
                      )}
                    </Flex>
                  </Td>
                  <Td isNumeric>
                    <Flex gap='8px' justify='flex-end'>
                      <Tooltip label='Editar' hasArrow>
                        <IconButton
                          aria-label='Editar grupo'
                          icon={<FiEdit2 />}
                          size='sm'
                          variant='ghost'
                          onClick={() => abrirEdicao(grupo)}
                        />
                      </Tooltip>
                      <Tooltip label='Excluir' hasArrow>
                        <IconButton
                          aria-label='Excluir grupo'
                          icon={<FiTrash2 />}
                          size='sm'
                          variant='ghost'
                          colorScheme='red'
                          onClick={() => setEmExclusao(grupo)}
                        />
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {filtrados.length === 0 && (
            <Text px='16px' py='24px' color='#818A8F'>
              {grupos?.length
                ? 'Nenhum grupo para esta busca.'
                : 'Nenhum grupo de comparação cadastrado.'}
            </Text>
          )}
        </TableContainer>
      )}

      <ComparisonGroupModal
        isOpen={formAberto}
        onClose={() => setFormAberto(false)}
        group={editando}
        onSaved={mutate}
      />

      {/* CA04: a consequência dita por extenso, com o tamanho dela. */}
      {/* `onClose` é o botão "Não" e `submit` é o "Sim" — o componente não tem
          prop `cancel`. Inverter isso faz a recusa executar a exclusão. */}
      <AttentionModal
        isOpen={!!emExclusao}
        onClose={() => setEmExclusao(null)}
        submit={confirmarExclusao}
        isLoading={excluindo}
        title='Excluir grupo de comparação'
        content={`As ${
          emExclusao?.total_empresas ?? 0
        } empresas de "${emExclusao?.name || ''}" deixarão de ter comparação de mercado nos dashboards de benchmarking.`}
      />
    </MainContent>
  );
}
